import React, {
  type ReactNode,
  useLayoutEffect,
  useRef,
  useEffect,
} from 'react';
import cx from 'classnames';
import scrollIntoView from 'scroll-into-view-if-needed';
import { Checkmark } from '@design-systems/icons';
import { mcdsFlagCheck } from '@mc/wink/helpers/utils-ts';

import stylesheet from './Option.css';
import {
  CheckboxDefault,
  CheckboxChecked,
  CheckboxDisabled,
  CheckboxDisabledChecked,
} from './ListboxIcons';

export type OptionProps = {
  children?: React.ReactNode;
  disabled?: boolean;
  highlightSelected?: boolean;
  id?: string;
  isFiltered?: boolean;
  isHighlighted?: boolean;
  isSelected?: boolean;
  onClick?: () => void;
  onHighlight?: (value: string | React.ReactNode) => void;
  value?: string | number;
  multiple?: boolean;
  hide?: boolean;
  label?: React.ReactNode;
  addOption?: boolean;
  description?: React.ReactNode;
};

function Option({
  value,
  children,
  disabled = false,
  highlightSelected = false,
  id,
  isFiltered = false,
  isSelected,
  isHighlighted = false,
  onClick,
  onHighlight,
  multiple,
  hide = false,
  label,
  addOption = false,
  description,
}: OptionProps) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (isHighlighted && ref.current) {
      scrollIntoView(ref.current, {
        block: 'nearest',
        scrollMode: 'if-needed',
        boundary: ref.current?.parentNode as Element,
      });
    }
  }, [isHighlighted]);

  const isBugFixDFlagOn = mcdsFlagCheck('xp_mcds_redesign_bug_fixes_D');

  useEffect(() => {
    if (!isBugFixDFlagOn) {
      return;
    }
    // Add .custom-focus-state class when keypress on MenuItem is detected
    const handleKeyDown = () => {
      // eslint-disable-next-line no-unused-expressions
      ref.current?.classList.add(stylesheet['custom-focus-state']);
    };
    document.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => {
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, [isBugFixDFlagOn]);

  if (isFiltered) {
    return null;
  }

  let CheckboxIcon: ReactNode = null;
  if (disabled && isSelected) {
    CheckboxIcon = <CheckboxDisabledChecked />;
  } else if (isSelected) {
    CheckboxIcon = <CheckboxChecked />;
  } else if (disabled) {
    CheckboxIcon = <CheckboxDisabled />;
  } else {
    CheckboxIcon = <CheckboxDefault />;
  }

  return (
    <div
      ref={ref}
      aria-disabled={disabled || undefined}
      aria-selected={isSelected}
      role="option"
      id={id}
      className={cx(stylesheet.option, {
        [stylesheet.highlightSelected]: highlightSelected,
        [stylesheet.isHighlighted]: isHighlighted,
        [stylesheet.addOption]: addOption,
      })}
      onClick={() => !disabled && onClick && onClick()}
      onMouseMove={() =>
        !isBugFixDFlagOn && !disabled && onHighlight && onHighlight(value)
      }
      data-label={label}
    >
      {multiple ? (
        !hide && CheckboxIcon
      ) : (
        <Checkmark className={stylesheet.selectedIcon} width={20} height={20} />
      )}

      <div className={stylesheet.optionContent}>
        {/* Hide label if its multi select since checkbox for multi select renders a label */}
        {children || value}
        {description && (
          <div className={stylesheet.description}>{description}</div>
        )}
      </div>
    </div>
  );
}

export default Option;
